import {
  apiChannelSubtype,
  isChatbotApiSubtype,
  isConciergeApiSubtype,
  isVoiceApiSubtype,
} from './apiChannelSubtype';
import { getComponentChildren } from './componentChildren';

const INBOX_ROUTE_PATTERN = /^\/app\/accounts\/\d+\/inbox\/(\d+)$/;
const EMAIL_CHANNEL_TYPE = 'Channel::Email';
const API_CHANNEL_TYPE = 'Channel::Api';
const MESSAGING_CHANNEL_TYPES = [
  'Channel::Telegram',
  'Channel::Whatsapp',
  'Channel::WhatsappBusiness',
];
const MODERN_EMAIL_PROVIDERS = ['google', 'microsoft'];
const UNREAD_EXCLUDED_TITLES = [
  'SENT',
  'SENT ITEMS',
  'SENT MESSAGES',
  'POSTA INVIATA',
  'DRAFT',
  'DRAFTS',
  'BOZZE',
  'TRASH',
  'DELETED',
  'DELETED ITEMS',
  'DELETED MESSAGES',
  'CESTINO',
  'POSTA ELIMINATA',
  'SPAM',
  'JUNK',
  'JUNK E-MAIL',
  'BULK MAIL',
  'POSTA INDESIDERATA',
  'UNREAD',
  'CHAT',
];
const UNREAD_EXCLUDED_WELL_KNOWN_NAMES = [
  'deleteditems',
  'junkemail',
  'sentitems',
  'drafts',
];
const GMAIL_GROUPED_LABEL_TITLES = ['CHAT', 'SPAM', 'TRASH'];
const NON_MODIFIABLE_DRAG_TARGET_TITLES = [
  'CATEGORY_PERSONAL',
  'CATEGORY_SOCIAL',
  'CATEGORY_PROMOTIONS',
  'CATEGORY_UPDATES',
  'CATEGORY_FORUMS',
  'CHAT',
  'SENT',
  'SENT ITEMS',
  'SENT MESSAGES',
  'DRAFT',
  'DRAFTS',
  'OUTBOX',
];
const MODIFIABLE_DRAG_TARGET_TITLES = [
  'INBOX',
  'TRASH',
  'SPAM',
  'UNREAD',
  'STARRED',
  'IMPORTANT',
  'ARCHIVE',
];
const NON_MODIFIABLE_DRAG_TARGET_WELL_KNOWN_NAMES = [
  'drafts',
  'outbox',
  'sentitems',
];
const MODIFIABLE_DRAG_TARGET_WELL_KNOWN_NAMES = [
  'archive',
  'deleteditems',
  'inbox',
  'junkemail',
];
const DRAG_TARGET_TITLE_ALIASES = {
  ARCHIVIO: 'ARCHIVE',
  BOZZE: 'DRAFT',
  'BULK MAIL': 'SPAM',
  'DELETED ITEMS': 'TRASH',
  'DELETED MESSAGES': 'TRASH',
  'ELEMENTI ELIMINATI': 'TRASH',
  'ELEMENTI INVIATI': 'SENT',
  'ELIMINA ELEMENTI': 'TRASH',
  JUNK: 'SPAM',
  'JUNK E-MAIL': 'SPAM',
  'POSTA ELIMINATA': 'TRASH',
  'POSTA IN ARRIVO': 'INBOX',
  'POSTA INDESIDERATA': 'SPAM',
  'POSTA IN USCITA': 'OUTBOX',
  'POSTA INVIATA': 'SENT',
};
const SIDEBAR_STATUS_LABELS = {
  open: 'Posta in arrivo',
  resolved: 'Archivio',
  sent: 'Posta inviata',
  draft: 'Bozze',
  spam: 'Spam',
  trash: 'Cestino',
};
// Badge del concierge: il campanello da reception, stesso grigio (#616060) e
// stesso tratto degli altri badge. E' un data URI SVG e non un PNG sul bucket
// come gli altri: niente upload da fare, la CSP ammette `data:` per le
// immagini e il disegno resta leggibile qui. In tema scuro lo ricolorano le
// stesse regole dei badge grigi di chatbot e voice, agganciate al prefisso
// `data:image/svg+xml` (cercare `sidebar-inbox-icon` e
// `inbox-channel-icon__image`).
const CONCIERGE_BADGE_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 39 39" fill="none" stroke="#616060" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">',
  '<path d="M7.6 26.2a11.4 11.4 0 0 1 22.8 0Z"/>',
  '<path d="M5.6 32.2h26.8"/>',
  '<path d="M19 14.6v-3.4"/>',
  '<path d="M15.4 10.6h7.2"/>',
  '<path d="M32 3.2c.5 2.9 1.5 3.9 4.4 4.4-2.9.5-3.9 1.5-4.4 4.4-.5-2.9-1.5-3.9-4.4-4.4 2.9-.5 3.9-1.5 4.4-4.4Z" fill="#616060" stroke-width="1.2"/>',
  '</svg>',
].join('');
const SIDEBAR_INBOX_ICON_URLS = {
  apple:
    'https://storage.googleapis.com/template-otello/web_icons/apple_mail_badge.png',
  chatbot:
    'https://storage.googleapis.com/template-otello/web_icons/chatbot_badge.png',
  concierge: `data:image/svg+xml,${encodeURIComponent(CONCIERGE_BADGE_SVG)}`,
  gmail:
    'https://storage.googleapis.com/template-otello/web_icons/gmail_badge.png',
  imap: 'https://storage.googleapis.com/template-otello/web_icons/imap_badge.png',
  mail: 'https://storage.googleapis.com/template-otello/web_icons/mail_badge.png',
  outlook:
    'https://storage.googleapis.com/template-otello/web_icons/outlook_badge.png',
  telegram:
    'https://storage.googleapis.com/template-otello/web_icons/telegram_badge.png',
  voice:
    'https://storage.googleapis.com/template-otello/web_icons/voice_badge.png',
  whatsapp:
    'https://storage.googleapis.com/template-otello/web_icons/whatsapp_badge.png',
  yahoo:
    'https://storage.googleapis.com/template-otello/web_icons/yahoo_badge.png',
};

const buildVirtualScheduledLabel = () => ({
  id: 'virtual-scheduled',
  title: 'SCHEDULED',
  folder_type: 'virtual-scheduled',
  show_on_sidebar: true,
  sub_labels: [],
  color: null,
  parent_label_id: null,
});

const buildVirtualAiRepliedLabel = () => ({
  id: 'virtual-ai-replied',
  title: 'AI_REPLIED',
  folder_type: 'virtual-ai-replied',
  show_on_sidebar: true,
  sub_labels: [],
  color: null,
  parent_label_id: null,
});

const buildVirtualAltroLabel = (subLabels) => ({
  id: 'virtual-altro',
  title: 'Altro',
  folder_type: 'virtual',
  show_on_sidebar: true,
  sub_labels: subLabels,
  color: null,
  parent_label_id: null,
});

const isEmailChannel = (labelObject) =>
  labelObject?.type === EMAIL_CHANNEL_TYPE;

const hasModernEmailProvider = (labelObject) =>
  MODERN_EMAIL_PROVIDERS.includes(labelObject?.provider);

const hasSyncActive = (labelObject) => labelObject?.sync_active === true;

const parseNumericId = (value) => {
  const parsedValue = parseInt(value, 10);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const isChatListComponent = (component) =>
  !!component &&
  (component?.$options?.name === 'ChatList' ||
    typeof component?.resetAndFetchData === 'function');

export function getSidebarInboxCount(inboxes) {
  return Array.isArray(inboxes) ? inboxes.length : 0;
}

export function getSidebarInboxLabelsForInbox(labelsByInbox, inboxId) {
  if (!labelsByInbox || inboxId == null) {
    return null;
  }

  const labels = labelsByInbox[inboxId] || labelsByInbox[String(inboxId)];
  return Array.isArray(labels) ? labels : null;
}

export function isSidebarPathWithinTarget({ currentPath, targetPath } = {}) {
  return (
    typeof currentPath === 'string' &&
    typeof targetPath === 'string' &&
    targetPath !== '' &&
    currentPath.startsWith(targetPath)
  );
}

export function resolveSidebarLabelNumericId(label) {
  return Number.isInteger(label?.id) ? label.id : parseNumericId(label?.id);
}

export function isSidebarEmailLabelActive({
  activeLabelId = null,
  label,
} = {}) {
  if (activeLabelId == null || label?.id == null) {
    return false;
  }

  return String(activeLabelId) === String(label.id);
}

export function shouldReloadSidebarLabelsForEvent({
  currentInboxId = null,
  event = null,
  isDestroyed = false,
} = {}) {
  const eventInboxId = event?.detail?.inboxId;

  if (isDestroyed || currentInboxId == null || eventInboxId == null) {
    return false;
  }

  return String(eventInboxId) === String(currentInboxId);
}

export function findSidebarChatListComponent({
  parentComponent = null,
  rootComponent = null,
} = {}) {
  let currentComponent = parentComponent;

  while (currentComponent) {
    if (isChatListComponent(currentComponent)) {
      return currentComponent;
    }

    currentComponent = currentComponent.$parent;
  }

  const componentStack = rootComponent ? [rootComponent] : [];
  while (componentStack.length > 0) {
    const component = componentStack.shift();

    if (isChatListComponent(component)) {
      return component;
    }

    const childComponents = getComponentChildren(component);
    if (childComponents.length) {
      componentStack.push(...childComponents);
    }
  }

  return null;
}

export function parseInboxIdFromPath(path) {
  if (typeof path !== 'string') {
    return null;
  }

  const match = path.match(INBOX_ROUTE_PATTERN);
  return match ? parseInt(match[1], 10) : null;
}

export function getRouteInboxId(route) {
  const rawInboxId = route?.params?.inbox_id || route?.params?.inboxId;

  return parseNumericId(rawInboxId);
}

export function buildSidebarStatusLink(path = '', status = '') {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}status=${status}`;
}

const isInboxPath = (path) => parseInboxIdFromPath(path) !== null;

const isRouteScopedToInbox = ({ inboxId, route } = {}) =>
  Boolean(inboxId) && getRouteInboxId(route) === inboxId;

export function isSidebarRouteForInbox({ inboxId, route } = {}) {
  return isRouteScopedToInbox({ inboxId, route });
}

const isSameRouteInbox = ({ inboxId, route } = {}) => {
  const routeInboxId = getRouteInboxId(route);

  return routeInboxId !== null && String(routeInboxId) === String(inboxId);
};

export function isSidebarConversationRoute(route) {
  return Boolean(
    route?.params?.conversation_id ||
    route?.name === 'conversation_through_inbox' ||
    route?.name === 'inbox_conversation'
  );
}

export function isModernEmailInbox({ labelObject, path } = {}) {
  return (
    isInboxPath(path) &&
    isEmailChannel(labelObject) &&
    (hasModernEmailProvider(labelObject) || hasSyncActive(labelObject))
  );
}

export function isTraditionalEmailInbox({ labelObject, path } = {}) {
  return (
    isInboxPath(path) &&
    isEmailChannel(labelObject) &&
    !hasModernEmailProvider(labelObject) &&
    !hasSyncActive(labelObject)
  );
}

export function isAnyEmailInbox({ labelObject, path } = {}) {
  return isInboxPath(path) && isEmailChannel(labelObject);
}

export function isApiBotInbox({ labelObject, path } = {}) {
  return isInboxPath(path) && labelObject?.type === API_CHANNEL_TYPE;
}

export function isMessagingInbox({ labelObject, path } = {}) {
  return (
    isInboxPath(path) && MESSAGING_CHANNEL_TYPES.includes(labelObject?.type)
  );
}

export function isInboxWithTraditionalSubmenu({ labelObject, path } = {}) {
  const isGoogleEmailInbox =
    isEmailChannel(labelObject) && labelObject?.provider === 'google';

  return isInboxPath(path) && !isGoogleEmailInbox;
}

export function resolveSidebarInboxIcon(labelObject) {
  if (!labelObject) {
    return SIDEBAR_INBOX_ICON_URLS.mail;
  }

  const rawProvider = labelObject?.provider;
  const provider =
    typeof rawProvider === 'string' ? rawProvider.toLowerCase() : '';
  const channelType = labelObject.type;

  if (channelType === EMAIL_CHANNEL_TYPE) {
    const hasImapCapabilities =
      labelObject?.imap_enabled || labelObject?.sync_active;
    const providerLooksLikeImap = provider.startsWith('imap');
    const missingProviderButImap =
      provider === '' && Boolean(hasImapCapabilities);

    if (providerLooksLikeImap || missingProviderButImap) {
      return SIDEBAR_INBOX_ICON_URLS.imap;
    }

    switch (provider) {
      case 'google':
        return SIDEBAR_INBOX_ICON_URLS.gmail;
      case 'microsoft':
      case 'outlook':
        return SIDEBAR_INBOX_ICON_URLS.outlook;
      case 'apple':
        return SIDEBAR_INBOX_ICON_URLS.apple;
      case 'yahoo':
        return SIDEBAR_INBOX_ICON_URLS.yahoo;
      default:
        return SIDEBAR_INBOX_ICON_URLS.mail;
    }
  }

  if (channelType === API_CHANNEL_TYPE) {
    const channelSubtype = apiChannelSubtype(labelObject);

    if (isVoiceApiSubtype(channelSubtype)) {
      return SIDEBAR_INBOX_ICON_URLS.voice;
    }
    if (isChatbotApiSubtype(channelSubtype)) {
      return SIDEBAR_INBOX_ICON_URLS.chatbot;
    }
    // Il concierge ha il suo badge: nella barra laterale la casella degli
    // ospiti in struttura non si confonde con quella del chatbot del sito.
    if (isConciergeApiSubtype(channelSubtype)) {
      return SIDEBAR_INBOX_ICON_URLS.concierge;
    }
    // Sottotipo sconosciuto: niente badge del chatbot, vale il ripiego
    // generico qui sotto.
  }

  switch (channelType) {
    case 'Channel::Telegram':
      return SIDEBAR_INBOX_ICON_URLS.telegram;
    case 'Channel::Whatsapp':
    case 'Channel::WhatsappBusiness':
      return SIDEBAR_INBOX_ICON_URLS.whatsapp;
    default:
      return SIDEBAR_INBOX_ICON_URLS.mail;
  }
}

export function resolveSidebarEmailProvider(labelObject) {
  const provider = labelObject?.provider;

  if (provider === 'google') {
    return 'gmail';
  }

  if (provider === 'microsoft') {
    return 'outlook';
  }

  if (labelObject?.imap_enabled || labelObject?.sync_active) {
    return 'imap';
  }

  return 'imap';
}

export function shouldSidebarInboxRootBeActive({
  filters = null,
  inboxId = null,
  isAPIBotInbox = false,
  isMessagingInbox: isMessagingInboxRoute = false,
  isTraditionalEmailInbox: isTraditionalEmailInboxRoute = false,
  route = {},
} = {}) {
  if (
    !isTraditionalEmailInboxRoute &&
    !isAPIBotInbox &&
    !isMessagingInboxRoute
  ) {
    return true;
  }

  if (filters?.status && filters.inboxId === inboxId) {
    return false;
  }

  if (filters?.labels && filters.inboxId === inboxId) {
    return false;
  }

  if (route?.query && Object.keys(route.query).length > 0) {
    const routeInboxId = parseInt(route?.params?.inboxId, 10);

    if (routeInboxId === inboxId) {
      return false;
    }
  }

  return !filters || (!filters.status && !filters.labels);
}

export function isActiveInboxStatus({
  filters,
  inboxId,
  route,
  status = 'open',
} = {}) {
  if (!isRouteScopedToInbox({ inboxId, route })) {
    return false;
  }

  const currentStatus = filters?.status;
  if (status === 'open') {
    return !currentStatus || currentStatus === 'open';
  }

  return currentStatus === status;
}

export function isActiveInboxView({ inboxId, route, view } = {}) {
  return (
    isRouteScopedToInbox({ inboxId, route }) && route?.query?.view === view
  );
}

export function isActiveInboxAiReplied({ inboxId, route } = {}) {
  return (
    isRouteScopedToInbox({ inboxId, route }) &&
    route?.query?.ai_replied === 'true'
  );
}

export function resolveSidebarActiveLabelIdFromRoute(route = {}) {
  const labelId = parseNumericId(route?.query?.label_id);

  if (labelId !== null) {
    return labelId;
  }

  return parseNumericId(route?.query?.labels);
}

export function shouldOpenSidebarDropdownOnMount({
  totalInboxes = 0,
  isCurrentInboxActive = false,
  isAnyEmailInbox: isEmailInbox = false,
  isArchiveInbox = false,
} = {}) {
  if (isArchiveInbox) return false;

  if (totalInboxes <= 3 || isCurrentInboxActive) {
    return true;
  }

  return !isEmailInbox;
}

export function isUnreadExcludedEmailLabel(label) {
  const title = (label?.title || '').toUpperCase();
  const wellKnownName = (label?.well_known_name || '').toLowerCase();

  return (
    UNREAD_EXCLUDED_TITLES.includes(title) ||
    UNREAD_EXCLUDED_WELL_KNOWN_NAMES.includes(wellKnownName)
  );
}

export function hasVisibleUnreadEmailLabel(labels = []) {
  if (!Array.isArray(labels)) {
    return false;
  }

  return labels.some((label) => {
    if (!label?.has_unread) {
      return false;
    }

    return !isUnreadExcludedEmailLabel(label);
  });
}

export function collectSidebarUnreadFlagUpdates({
  sourceLabels = [],
  targetLabels = [],
} = {}) {
  const unreadById = new Map();

  const collectSourceLabels = (labels) => {
    if (!Array.isArray(labels)) {
      return;
    }

    labels.forEach((label) => {
      if (label?.id != null) {
        unreadById.set(String(label.id), Boolean(label.has_unread));
      }

      collectSourceLabels(label?.sub_labels);
    });
  };

  const collectTargetUpdates = (labels, updates = []) => {
    if (!Array.isArray(labels)) {
      return updates;
    }

    labels.forEach((label) => {
      const idKey = label?.id != null ? String(label.id) : null;

      if (idKey && unreadById.has(idKey)) {
        const hasUnread = unreadById.get(idKey);
        if (label.has_unread !== hasUnread) {
          updates.push({ label, hasUnread });
        }
      }

      collectTargetUpdates(label?.sub_labels, updates);
    });

    return updates;
  };

  collectSourceLabels(sourceLabels);

  return collectTargetUpdates(targetLabels);
}

export function runSidebarUnreadFlagSync({
  applyUnreadFlag = null,
  sourceLabels = [],
  targetLabels = [],
} = {}) {
  if (typeof applyUnreadFlag !== 'function') {
    return { status: 'invalid', updates: [] };
  }

  const updates = collectSidebarUnreadFlagUpdates({
    sourceLabels,
    targetLabels,
  });

  updates.forEach(({ label, hasUnread }) => {
    applyUnreadFlag(label, hasUnread);
  });

  return {
    status: updates.length > 0 ? 'synced' : 'idle',
    updates,
  };
}

const collectDescendantLabelKeys = (labels) => {
  const descendantIds = new Set();
  const descendantSourceIds = new Set();

  const visit = (label) => {
    if (!label || !Array.isArray(label.sub_labels)) {
      return;
    }

    label.sub_labels.forEach((child) => {
      if (child?.id != null) {
        descendantIds.add(String(child.id));
      }
      if (child?.source_id != null) {
        descendantSourceIds.add(String(child.source_id));
      }
      visit(child);
    });
  };

  labels.forEach((label) => visit(label));

  return { descendantIds, descendantSourceIds };
};

const isDescendantLabel = (label, { descendantIds, descendantSourceIds }) => {
  const idKey = label?.id != null ? String(label.id) : null;
  const sourceIdKey = label?.source_id != null ? String(label.source_id) : null;

  return (
    (idKey && descendantIds.has(idKey)) ||
    (sourceIdKey && descendantSourceIds.has(sourceIdKey))
  );
};

const keepDirectSubLabels = (label) => ({
  ...label,
  sub_labels: Array.isArray(label.sub_labels)
    ? label.sub_labels.filter(
        (subLabel) => subLabel.parent_label_id === label.id
      )
    : label.sub_labels,
});

export function buildSidebarInboxLabels({ labels = [], provider } = {}) {
  const allLabels = Array.isArray(labels) ? labels : [];
  const scheduledLabel = buildVirtualScheduledLabel();
  const aiRepliedLabel = buildVirtualAiRepliedLabel();

  if (provider === 'microsoft' || !provider) {
    const visibleLabels = allLabels.filter((label) => label?.show_on_sidebar);
    const descendantKeys = collectDescendantLabelKeys(allLabels);
    const rootLabels = visibleLabels.filter(
      (label) => !isDescendantLabel(label, descendantKeys)
    );

    return [...rootLabels, scheduledLabel, aiRepliedLabel];
  }

  const labelsToShow = allLabels
    .filter((label) => label?.show_on_sidebar)
    .map(keepDirectSubLabels);

  const groupedLabels = labelsToShow.filter((label) =>
    GMAIL_GROUPED_LABEL_TITLES.includes(label.title)
  );
  const systemLabels = labelsToShow.filter(
    (label) =>
      !GMAIL_GROUPED_LABEL_TITLES.includes(label.title) &&
      label.folder_type === 'system'
  );
  const userLabels = labelsToShow.filter(
    (label) => label.folder_type === 'user'
  );

  if (groupedLabels.length > 0) {
    return [
      ...systemLabels,
      scheduledLabel,
      aiRepliedLabel,
      buildVirtualAltroLabel(groupedLabels),
      ...userLabels,
    ];
  }

  return [...systemLabels, scheduledLabel, aiRepliedLabel, ...userLabels];
}

export function isEmailLabelDragTarget({ isEmailInbox, label } = {}) {
  if (!isEmailInbox || !label) {
    return false;
  }

  const labelId = String(label.id || '').toLowerCase();
  const folderType = String(label.folder_type || '').toLowerCase();
  if (labelId.startsWith('virtual-') || folderType.startsWith('virtual')) {
    return false;
  }

  const rawLabelTitle = String(label.title || '').toUpperCase();
  const labelTitle = DRAG_TARGET_TITLE_ALIASES[rawLabelTitle] || rawLabelTitle;
  const sourceId = String(label.source_id || '').toUpperCase();
  const wellKnownName = String(label.well_known_name || '').toLowerCase();

  if (
    NON_MODIFIABLE_DRAG_TARGET_TITLES.includes(labelTitle) ||
    NON_MODIFIABLE_DRAG_TARGET_WELL_KNOWN_NAMES.includes(wellKnownName)
  ) {
    return false;
  }

  return (
    MODIFIABLE_DRAG_TARGET_TITLES.includes(labelTitle) ||
    MODIFIABLE_DRAG_TARGET_WELL_KNOWN_NAMES.includes(wellKnownName) ||
    folderType === 'user' ||
    labelTitle.startsWith('LABEL_') ||
    sourceId.startsWith('LABEL_')
  );
}

export function shouldSkipSidebarLabelNavigation({
  inboxId,
  label,
  route,
} = {}) {
  if (
    isSidebarConversationRoute(route) ||
    !isSameRouteInbox({ inboxId, route })
  ) {
    return false;
  }

  const normalizeLabelRouteValue = (value) =>
    value == null ? null : String(value).toLowerCase();
  const routeLabelSourceId = normalizeLabelRouteValue(
    route?.query?.label_source_id
  );
  const clickedLabelSourceMatches =
    !routeLabelSourceId ||
    [label?.source_id, label?.sourceId, label?.id, label?.title].some(
      (value) => normalizeLabelRouteValue(value) === routeLabelSourceId
    );
  const sameLabelId =
    route?.query?.label_id &&
    String(route.query.label_id) === String(label?.id) &&
    clickedLabelSourceMatches;
  const sameLabelTitle =
    !route?.query?.label_id &&
    route?.query?.labels &&
    String(route.query.labels) === String(label?.title);

  return Boolean(sameLabelId || sameLabelTitle);
}

export function shouldSkipSidebarStatusNavigation({
  inboxId,
  route,
  status,
} = {}) {
  if (!isSameRouteInbox({ inboxId, route })) {
    return false;
  }

  const currentStatus = route?.query?.status || 'open';

  return String(currentStatus) === String(status);
}

export function shouldSkipSidebarViewNavigation({ inboxId, route, view } = {}) {
  return isSameRouteInbox({ inboxId, route }) && route?.query?.view === view;
}

export function shouldSkipSidebarAiRepliedNavigation({ inboxId, route } = {}) {
  return (
    isSameRouteInbox({ inboxId, route }) && route?.query?.ai_replied === 'true'
  );
}

export function buildInboxDashboardRoute({ accountId, inboxId, query = {} }) {
  return {
    name: 'inbox_dashboard',
    params: {
      accountId,
      inbox_id: inboxId,
    },
    query,
  };
}

export function buildSidebarLabelRoute({ accountId, inboxId, label }) {
  return buildInboxDashboardRoute({
    accountId,
    inboxId,
    query: {
      labels: label?.title,
      label_id: label?.id || undefined,
      label_source_id: label?.source_id || undefined,
    },
  });
}

export function buildSidebarStatusQuery(status) {
  return status === 'open' ? {} : { status };
}

export function getSidebarStatusLabel(status) {
  return SIDEBAR_STATUS_LABELS[status] || status;
}
